import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card,
  Heading,
  Container,
  Spacer,
  Button,
  Icon,
  Flex,
  Stack,
  Center,
  Spinner,
  Box,
  Text,
  Tabs,
  useBreakpointValue,
} from '@chakra-ui/react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { NavLink, useParams } from 'react-router-dom';
import Sort from './elements/sort';
import Search from './elements/search';
import CollectionList from './collection-list';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsUser from '@/lib/hooks/use-is-user';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useDebounce from '@/lib/hooks/use-debounce';
import useCurrentUser from '@/lib/hooks/use-current-user';
import useCollectionData from './hooks/use-collection-data';
import useCollectionState from './hooks/use-collection-state';
import useCollectionTypeStore from '@/utils/use-collection-type';
import { CollectionType } from '@/lib/types';

export default function Collections() {
  const { workspaceId, collectionTypeId, tabIndex } = useParams<{
    workspaceId: string;
    collectionTypeId: string;
    tabIndex: string;
  }>();
  const isAdmin = useIsInAdminGroup();
  const isUser = useIsUser();
  const isCancelled = useIsCancelled();
  const { user } = useCurrentUser();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const [collectionType, setCollectionType] = useState<CollectionType>();
  const { collectionTypes } = useCollectionTypeStore();

  const initializingRef = useRef(false);
  const observer = useRef<IntersectionObserver | null>(null);

  const {
    searchQuery,
    setSearchQuery,
    sortKey,
    setSortKey,
    filters,
    setFilters,
    page,
    setPage,
    initialized,
    setInitialized,
    resetState,
  } = useCollectionState();

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const myCollection = useCallback(
    async (userId: number) => {
      resetState();
      setFilters([{ key: 'created_by', val: userId.toString() }]);
    },
    [setFilters, resetState],
  );

  const allWorkspaceCollection = useCallback(() => {
    resetState();
    setFilters([{ key: 'status', val: 'published' }]);
  }, [setFilters, resetState]);

  useEffect(() => {
    if (collectionTypeId) {
      const collectionType = [...collectionTypes].find(
        (type) => type.id === parseInt(collectionTypeId, 10),
      );

      if (collectionType) {
        setCollectionType(collectionType);
      }
    }
  }, [collectionTypes, collectionTypeId]);

  useEffect(() => {
    const initializeTabContent = async () => {
      if (!tabIndex || !user?.id) return;

      if (initializingRef.current) return;
      initializingRef.current = true;

      try {
        setInitialized(false);

        switch (tabIndex) {
          case 'my-collections':
            await myCollection(user.id);
            break;
          case 'workspace-collections':
            await allWorkspaceCollection();
            break;
        }

        setInitialized(true);
      } finally {
        initializingRef.current = false;
      }
    };

    initializeTabContent();
  }, [collectionTypeId, tabIndex, user, setInitialized]);

  const { collections, isLoading, hasMore, reloadCollections } = useCollectionData({
    initialized,
    collectionTypeId,
    filters,
    sortKey,
    searchQuery: debouncedSearchQuery,
    pageNumber: page,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleSortChange = (value: string) => {
    setSortKey(value);
    setPage(1);
  };

  const lastContentElementRef = useCallback(
    (node: any) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage(page + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, page, setPage],
  );

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header>
        <Container>
          <Flex mb={4}>
            <Heading size={'3xl'}>{collectionType && collectionType.name}</Heading>
            <Spacer />
            <Box display={{ base: 'none', md: 'block' }} w={'50%'}>
              <Search
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={() => setSearchQuery('')}
              />
            </Box>
            <Spacer />
            {(isAdmin || isUser) && !isCancelled && (
              <Button colorPalette='brand' asChild>
                <NavLink
                  to={`/${workspaceId}/collection-types/${collectionTypeId}/collections/create`}
                >
                  <Icon as={PlusIcon} w={4} h={4} />
                  Create
                </NavLink>
              </Button>
            )}
          </Flex>
        </Container>
      </Card.Header>
      <Card.Body>
        <Container>
          <Tabs.Root
            value={tabIndex}
            orientation={isSmallScreen ? 'vertical' : 'horizontal'}
            variant={isSmallScreen ? 'enclosed' : 'line'}
            size='lg'
          >
            <Tabs.List width={isSmallScreen ? 'full' : 'auto'}>
              <Tabs.Trigger value='workspace-collections' asChild>
                <Button colorPalette='brand' variant='ghost' asChild>
                  <NavLink
                    to={`/${workspaceId}/collection-types/${collectionTypeId}/collections/workspace-collections`}
                  >
                    Workspace Collections
                  </NavLink>
                </Button>
              </Tabs.Trigger>
              {(isAdmin || isUser) && (
                <Tabs.Trigger value='my-collections' asChild>
                  <Button colorPalette='brand' variant='ghost' asChild>
                    <NavLink
                      to={`/${workspaceId}/collection-types/${collectionTypeId}/collections/my-collections`}
                    >
                      My Collections
                    </NavLink>
                  </Button>
                </Tabs.Trigger>
              )}
            </Tabs.List>
          </Tabs.Root>

          <Box display={{ base: 'block', md: 'none' }} mt={3} w={'full'}>
            <Search
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onClearSearch={() => setSearchQuery('')}
            />
          </Box>
          <Stack mt={3} gap={{ base: 0, md: 3 }} direction={'row'}>
            <Sort sortKey={sortKey} onSortChange={handleSortChange} />
          </Stack>

          {workspaceId && collectionTypeId && (
            <CollectionList
              collections={collections}
              collectionTypeId={collectionTypeId}
              workspaceId={workspaceId}
              lastContentElementRef={lastContentElementRef}
              reloadCollections={reloadCollections}
            />
          )}

          {isLoading && (
            <Center mt={4}>
              <Spinner size={'lg'} />
            </Center>
          )}
          {!isLoading && collections.length === 0 && (
            <Text fontSize={'sm'} textAlign={'center'}>
              No collection available
            </Text>
          )}
        </Container>
      </Card.Body>
    </Card.Root>
  );
}
